import { useState, useEffect } from 'react'

interface AppIconProps {
  /** Material icon name — the default rendering (falls back to 'apps'). */
  icon?: string
  /** Uploaded image icon (data URL). When set it replaces the font icon. */
  imageData?: string
  /** Box size in px. */
  size: number
  /** Font-icon glyph size in px (defaults to ~55% of the box). */
  iconSize?: number
  /** Box background (ignored for the image — it fills the box). */
  background?: string
  /** Font-icon color. */
  color?: string
  borderRadius?: string | number
}

/**
 * Project/application icon badge. Renders the uploaded image icon when the
 * project has one, otherwise the material font icon exactly as before.
 * A broken image data URL falls back to the font icon instead of a hole.
 */
export default function AppIcon({
  icon,
  imageData,
  size,
  iconSize,
  background,
  color,
  borderRadius = 'var(--radius-md)',
}: AppIconProps) {
  const [imgFailed, setImgFailed] = useState(false)
  // A new image should get a fresh chance to load.
  useEffect(() => { setImgFailed(false) }, [imageData])

  const showImage = !!imageData && !imgFailed

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius,
        background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      {showImage ? (
        <img
          src={imageData}
          alt=""
          onError={() => setImgFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <span
          className="material-icons"
          style={{ fontSize: iconSize ?? Math.round(size * 0.55), color }}
        >
          {icon || 'apps'}
        </span>
      )}
    </div>
  )
}
